import type {
  StreamControlInput,
  StreamSessionId,
} from "@redotech/effect-api-shared/models/stream-protocol";
import {
  type RegisteredStreamChannelName,
  type StreamChannelSchemaFor,
  streamChannelSchemaRegistry,
} from "@redotech/effect-api-stream-schemas";
import {
  getSubscriptionKey,
  handleServerMessage,
  type LogicalSubscriptionRecord,
  notifySubscriptionError,
  parseEventData,
} from "@redotech/redo-web/stream/stream-message-handler";
import { Effect } from "effect";
import { createEventSource } from "eventsource-client";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "../auth";
import { REDO_EFFECT_API_URL } from "../config";
import {
  EffectApiClientContext,
  type EffectApiContext,
} from "../effect-api-client-provider";
import {
  type BackoffEventSourceHandle,
  createBackoffEventSource,
} from "./backoff-event-source";

interface StreamEventHandler<TInitial, TEvent> {
  readonly onEvent: (payload: TEvent) => void;
  readonly onInitial?: (payload: TInitial) => void;
  readonly onSubscribed?: () => void;
  readonly onError?: (message: string) => void;
  readonly onUnsubscribed?: () => void;
}

type StreamSubscribeOptions<TChannel extends RegisteredStreamChannelName> = {
  readonly channel: TChannel;
  readonly input?: StreamChannelSchemaFor<TChannel> extends {
    inputSchema: { _output: infer TInput };
  }
    ? TInput
    : never;
} & StreamEventHandler<
  StreamChannelSchemaFor<TChannel> extends {
    initialSchema: { _output: infer TInitial };
  }
    ? TInitial
    : never,
  StreamChannelSchemaFor<TChannel> extends {
    eventSchema: { _output: infer TEvent };
  }
    ? TEvent
    : never
>;

interface StreamContextValue {
  subscribe: <TChannel extends RegisteredStreamChannelName>(
    options: StreamSubscribeOptions<TChannel>,
  ) => () => void;
}

const StreamContext = createContext<StreamContextValue | null>(null);
StreamContext.displayName = "StreamContext";

/**
 * Live SSE connection status, kept in a context separate from
 * {@link StreamContext} so that toggling connectivity does not change the
 * `subscribe` context value and force every `useSubscribe` caller to tear down
 * and re-establish its subscription. Consumers that want to react to stream
 * health (e.g. enable a polling fallback while the stream is down) read this.
 */
const StreamConnectedContext = createContext<boolean>(false);
StreamConnectedContext.displayName = "StreamConnectedContext";

const STREAM_ENDPOINT_PATH = "/api/streams/team/stream";

async function openStreamSession(
  effectApi: EffectApiContext,
): Promise<StreamSessionId> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    client.stream.openTeamStream().pipe(Effect.orDie),
  );
  return result.sessionId as StreamSessionId;
}

async function controlStreamSession(
  effectApi: EffectApiContext,
  sessionId: StreamSessionId,
  body: StreamControlInput,
): Promise<void> {
  const client = effectApi.getClient();
  if (body.action === "subscribe") {
    await effectApi.runPromise(
      client.stream
        .controlTeamStream({ path: { sessionId }, payload: body })
        .pipe(Effect.orDie),
    );
  } else {
    await effectApi.runPromise(
      client.stream
        .controlTeamStream({ path: { sessionId }, payload: body })
        .pipe(Effect.orDie),
    );
  }
}

export const StreamProvider = memo(function StreamProvider({
  children,
}: {
  children: ReactNode;
}) {
  const auth = useContext(AuthContext);
  const effectApi = useContext(EffectApiClientContext);
  const subscriptionsRef = useRef<Map<string, LogicalSubscriptionRecord>>(
    new Map(),
  );
  const streamIdRef = useRef<StreamSessionId | undefined>(undefined);
  const backoffHandleRef = useRef<BackoffEventSourceHandle | undefined>(
    undefined,
  );
  const [isConnected, setIsConnected] = useState(false);

  const baseUrl = REDO_EFFECT_API_URL;
  const token = auth?.token;

  const closeStream = useCallback(() => {
    backoffHandleRef.current?.close();
    backoffHandleRef.current = undefined;
    streamIdRef.current = undefined;
    setIsConnected(false);
  }, []);

  const postControl = useCallback(
    async (sessionId: StreamSessionId, body: StreamControlInput) => {
      if (!effectApi) {
        return;
      }
      await controlStreamSession(effectApi, sessionId, body);
    },
    [effectApi],
  );

  const postControlSafe = useCallback(
    async (
      sessionId: StreamSessionId,
      body: StreamControlInput,
      onError: (message: string) => void,
    ) => {
      try {
        await postControl(sessionId, body);
      } catch (error) {
        onError(
          error instanceof Error
            ? error.message
            : "Failed to control stream session",
        );
      }
    },
    [postControl],
  );

  const onServerMessage = useCallback((raw: unknown) => {
    handleServerMessage(raw, subscriptionsRef.current);
  }, []);

  const resubscribeAll = useCallback(
    (sessionId: StreamSessionId) => {
      for (const subscription of subscriptionsRef.current.values()) {
        void postControlSafe(
          sessionId,
          {
            action: "subscribe",
            channel: subscription.channel,
            input: subscription.input,
            subscriptionKey: subscription.subscriptionKey,
          },
          (message) => {
            notifySubscriptionError(subscription, message);
          },
        );
      }
    },
    [postControlSafe],
  );

  const ensureStream = useCallback(() => {
    if (!token || !effectApi) {
      return;
    }
    if (backoffHandleRef.current != null) {
      return;
    }

    backoffHandleRef.current = createBackoffEventSource({
      logLabel: "team-stream",
      connect: async ({ onConnected, onDisconnected }) => {
        const sessionId = await openStreamSession(effectApi);
        const es = createEventSource({
          headers: { Authorization: `Bearer ${token}` },
          onConnect: () => {
            streamIdRef.current = sessionId;
            resubscribeAll(sessionId);
            setIsConnected(true);
            onConnected();
          },
          onScheduleReconnect: () => {
            streamIdRef.current = undefined;
            setIsConnected(false);
            es.close();
            onDisconnected();
          },
          onMessage: ({ data }: { data: string }) => {
            const parsed = parseEventData(data);
            if (parsed === undefined) {
              return;
            }
            onServerMessage(parsed);
          },
          url: new URL(
            `${STREAM_ENDPOINT_PATH}/${sessionId}`,
            baseUrl,
          ).toString(),
        });
        return { close: () => es.close() };
      },
    });
  }, [baseUrl, effectApi, onServerMessage, resubscribeAll, token]);

  const subscribe = useCallback<StreamContextValue["subscribe"]>(
    (options) => {
      const definition = streamChannelSchemaRegistry[options.channel];
      const parsedInput = definition.inputSchema.safeParse(options.input ?? {});
      if (!parsedInput.success) {
        throw new Error(`Invalid stream input for ${options.channel}`);
      }

      const input = parsedInput.data;
      const inputJson = JSON.stringify(input);
      const subscriptionKey = getSubscriptionKey(options.channel, inputJson);

      let subscription = subscriptionsRef.current.get(subscriptionKey);
      if (subscription == null) {
        subscription = {
          channel: options.channel,
          definition,
          handlers: new Map(),
          input,
          inputJson,
          subscriptionKey,
        };
        subscriptionsRef.current.set(subscriptionKey, subscription);
      }

      const handlerId = crypto.randomUUID();
      subscription.handlers.set(handlerId, {
        id: handlerId,
        onError: options.onError,
        onEvent: options.onEvent as (payload: unknown) => void,
        onInitial: options.onInitial as
          | ((payload: unknown) => void)
          | undefined,
        onSubscribed: options.onSubscribed,
        onUnsubscribed: options.onUnsubscribed,
      });

      if (subscription.lastInitialPayload !== undefined) {
        (options.onInitial as ((payload: unknown) => void) | undefined)?.(
          subscription.lastInitialPayload,
        );
      }

      const alreadyConnectedSessionId = streamIdRef.current;
      if (alreadyConnectedSessionId != null) {
        if (subscription.handlers.size === 1) {
          void postControlSafe(
            alreadyConnectedSessionId,
            {
              action: "subscribe",
              channel: subscription.channel,
              input: subscription.input,
              subscriptionKey,
            },
            (message) => {
              notifySubscriptionError(subscription, message);
            },
          );
        }
      } else {
        void ensureStream();
      }

      return () => {
        const current = subscriptionsRef.current.get(subscriptionKey);
        if (current == null) {
          return;
        }
        current.handlers.delete(handlerId);
        if (current.handlers.size > 0) {
          return;
        }

        subscriptionsRef.current.delete(subscriptionKey);
        const sessionId = streamIdRef.current;
        if (sessionId) {
          void postControlSafe(
            sessionId,
            { action: "unsubscribe", subscriptionKey },
            (message) => {
              notifySubscriptionError(current, message);
            },
          );
        }

        if (subscriptionsRef.current.size === 0) {
          closeStream();
        }
      };
    },
    [closeStream, ensureStream, postControlSafe],
  );

  useEffect(() => {
    if (token) {
      return;
    }
    subscriptionsRef.current.clear();
    closeStream();
  }, [closeStream, token]);

  useEffect(() => {
    if (!token || subscriptionsRef.current.size === 0) {
      return;
    }

    closeStream();
    void ensureStream();
  }, [closeStream, ensureStream, token]);

  useEffect(
    () => () => {
      closeStream();
    },
    [closeStream],
  );

  const value = useMemo<StreamContextValue>(() => ({ subscribe }), [subscribe]);

  return (
    <StreamContext.Provider value={value}>
      <StreamConnectedContext.Provider value={isConnected}>
        {children}
      </StreamConnectedContext.Provider>
    </StreamContext.Provider>
  );
});

/**
 * Whether the team SSE stream is currently connected. Use this to drive a
 * fallback (e.g. polling) only while realtime delivery is unavailable, instead
 * of polling unconditionally.
 */
export function useStreamConnected(): boolean {
  return useContext(StreamConnectedContext);
}

export function useSubscribe<TChannel extends RegisteredStreamChannelName>(
  options: StreamSubscribeOptions<TChannel>,
): void {
  const stream = useContext(StreamContext);
  if (stream == null) {
    throw new Error("useSubscribe must be used within StreamProvider");
  }

  const stableOptionsRef = useRef(options);
  stableOptionsRef.current = options;
  const serializedInput = JSON.stringify(options.input ?? {});

  useEffect(() => {
    return stream.subscribe(stableOptionsRef.current);
  }, [stream, options.channel, serializedInput]);
}
